import React, { useEffect, useId, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
	ActionIcon,
	Box,
	Button,
	Divider,
	Group,
	Loader,
	Popover,
	Text,
	createStyles,
} from '@mantine/core'
import { ArrowRight, X } from 'tabler-icons-react'
import useOrgSearch from '../hooks/useOrgSearch'
import OrgSearchResultContent from './OrgSearchResultContent'
import {
	ORG_SEARCH_CATEGORIES,
	ORG_SEARCH_MAX_LENGTH,
	ORG_SEARCH_MIN_LENGTH,
	orgSearchPath,
	orgSearchResultPath,
} from '../services/utils/orgSearch'

const useStyles = createStyles((theme) => ({
	searchInput: {
		'&::-webkit-search-cancel-button': { display: 'none' },
	},
	option: {
		width: '100%',
		display: 'block',
		textAlign: 'left',
		padding: '9px 10px',
		border: 0,
		borderRadius: theme.radius.sm,
		background: 'transparent',
		color: 'inherit',
		cursor: 'pointer',
		'&[aria-selected="true"], &:hover': {
			background:
				theme.colorScheme === 'dark'
					? theme.colors.dark[4]
					: theme.colors.blue[0],
		},
	},
}))

export default function OrgSearchBox({ orgSlug, className, inputClassName }) {
	const { classes, cx } = useStyles()
	const navigate = useNavigate()
	const location = useLocation()
	const listId = useId()
	const activeOptionRef = useRef(null)
	const inputRef = useRef(null)
	const [query, setQuery] = useState('')
	const [opened, setOpened] = useState(false)
	const [composing, setComposing] = useState(false)
	const [activeIndex, setActiveIndex] = useState(-1)
	const term = query.trim()
	const { data, loading, error, retry } = useOrgSearch(orgSlug, query, {
		enabled: opened && !composing,
	})
	const groups = data?.groups || []
	const items = groups.flatMap((group) => group.items)
	const expanded = opened && term.length > 0
	const activeItem = expanded ? items[activeIndex] : null
	const optionId = (item) => `${listId}-${item.type}-${item.id}`
	const close = () => {
		setOpened(false)
		setActiveIndex(-1)
	}

	useEffect(() => {
		setOpened(false)
		setActiveIndex(-1)
	}, [location.key, orgSlug])
	useEffect(() => {
		setActiveIndex(-1)
	}, [data])
	useEffect(() => {
		activeOptionRef.current?.scrollIntoView?.({ block: 'nearest' })
	}, [activeItem])

	const openDestination = (path) => {
		close()
		setQuery('')
		navigate(path)
	}
	const goToResults = () => {
		if (
			term.length < ORG_SEARCH_MIN_LENGTH ||
			term.length > ORG_SEARCH_MAX_LENGTH
		)
			return
		openDestination(orgSearchPath(orgSlug, term))
	}
	const goToItem = (item) => openDestination(orgSearchResultPath(orgSlug, item))
	const handleSubmit = (event) => {
		event.preventDefault()
		if (composing) return
		if (activeItem) goToItem(activeItem)
		else goToResults()
	}
	const handleKeyDown = (event) => {
		if (event.nativeEvent.isComposing || composing) {
			if (event.key === 'Enter') event.preventDefault()
			return
		}
		if (event.key === 'Escape') {
			event.preventDefault()
			close()
		}
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault()
			setOpened(true)
			if (!items.length) return
			setActiveIndex((current) =>
				event.key === 'ArrowDown'
					? (current + 1) % items.length
					: current <= 0
						? items.length - 1
						: current - 1,
			)
		}
	}
	// Keep focus on the combobox when a suggestion is clicked.
	const keepInputFocus = (event) => event.preventDefault()

	return (
		<Popover
			opened={expanded}
			onClose={close}
			position='top-start'
			width={420}
			withinPortal
			trapFocus={false}
			returnFocus={false}
			withRoles={false}
			zIndex={300}
			shadow='md'
		>
			<Popover.Target>
				<form
					className={className}
					style={{ margin: 0, flexShrink: 0, position: 'relative' }}
					onSubmit={handleSubmit}
					role='search'
					aria-label='Search organization'
				>
					<input
						ref={inputRef}
						className={cx(inputClassName, classes.searchInput)}
						style={{ paddingRight: 28 }}
						type='search'
						placeholder='Search'
						aria-label='Footer search'
						role='combobox'
						aria-autocomplete='list'
						aria-expanded={expanded}
						aria-controls={expanded ? listId : undefined}
						aria-activedescendant={
							activeItem ? optionId(activeItem) : undefined
						}
						value={query}
						maxLength={ORG_SEARCH_MAX_LENGTH}
						autoComplete='off'
						spellCheck={false}
						onChange={(event) => {
							setQuery(event.target.value)
							setActiveIndex(-1)
							setOpened(true)
						}}
						onFocus={() => setOpened(true)}
						onBlur={close}
						onKeyDown={handleKeyDown}
						onCompositionStart={() => setComposing(true)}
						onCompositionEnd={() => setComposing(false)}
					/>
					{query.length > 0 && (
						<ActionIcon
							type='button'
							aria-label='Clear search'
							variant='subtle'
							color='gray'
							size={20}
							style={{
								position: 'absolute',
								right: 3,
								top: '50%',
								transform: 'translateY(-50%)',
							}}
							onMouseDown={keepInputFocus}
							onClick={() => {
								setQuery('')
								inputRef.current?.focus()
								close()
							}}
						>
							<X size={14} strokeWidth={1.8} />
						</ActionIcon>
					)}
				</form>
			</Popover.Target>
			<Popover.Dropdown p={8} style={{ maxWidth: 'calc(100vw - 32px)' }}>
				<Group position='apart' px={10} py={6}>
					<Text size='xs' color='dimmed'>
						Search this organization
					</Text>
					<Text size='xs' color='dimmed'>
						↑ ↓ to select · Enter to open
					</Text>
				</Group>
				<Box
					id={listId}
					role='listbox'
					aria-label='Organization search suggestions'
					style={{ maxHeight: 'min(420px, 60vh)', overflowY: 'auto' }}
				>
					{term.length < ORG_SEARCH_MIN_LENGTH && (
						<Text size='sm' p={10}>
							Please type at least 2 characters.
						</Text>
					)}
					{loading && (
						<Group p={10} spacing='xs' role='status'>
							<Loader size='xs' />
							<Text size='sm'>Searching…</Text>
						</Group>
					)}
					{error && (
						<Group p={10}>
							<Text size='sm' color='red' role='alert'>
								{error}
							</Text>
							<Button
								size='xs'
								variant='subtle'
								tabIndex={-1}
								onMouseDown={keepInputFocus}
								onClick={retry}
							>
								Retry
							</Button>
						</Group>
					)}
					{data && !items.length && (
						<Text size='sm' p={10}>
							No matches found.
						</Text>
					)}
					{groups
						.filter((group) => group.items.length)
						.map((group) => (
							<div
								role='group'
								aria-label={
									ORG_SEARCH_CATEGORIES.find(
										(category) => category.value === group.type,
									)?.label
								}
								key={group.type}
							>
								<Text
									size='xs'
									weight={600}
									color='dimmed'
									px={10}
									pt={8}
									pb={4}
								>
									{
										ORG_SEARCH_CATEGORIES.find(
											(category) => category.value === group.type,
										)?.label
									}
								</Text>
								{group.items.map((item) => (
									<button
										type='button'
										role='option'
										id={optionId(item)}
										key={item.id}
										tabIndex={-1}
										className={classes.option}
										ref={activeItem === item ? activeOptionRef : null}
										aria-selected={activeItem === item}
										onMouseDown={keepInputFocus}
										onClick={() => goToItem(item)}
									>
										<OrgSearchResultContent item={item} />
									</button>
								))}
							</div>
						))}
				</Box>
				{term.length >= ORG_SEARCH_MIN_LENGTH && (
					<>
						<Divider my={8} />
						<Button
							fullWidth
							variant='subtle'
							size='xs'
							rightIcon={<ArrowRight size={14} />}
							tabIndex={-1}
							onMouseDown={keepInputFocus}
							onClick={goToResults}
						>
							View all results
						</Button>
					</>
				)}
			</Popover.Dropdown>
		</Popover>
	)
}
